/**
 * Typed fetch wrapper for the gateway.
 *
 * Cookies are always sent (`credentials: 'include'`) — the gateway sets
 * httpOnly access_token and refresh_token cookies on the same origin.
 * For cross-origin dev (Vite on :5173, gateway on :8100) the gateway
 * already allows the right CORS origins.
 *
 * `ApiError` wraps non-2xx responses so callers can introspect the
 * gateway's error code without parsing JSON manually each time.
 */
import { env } from '../../env';

export class ApiError extends Error {
  status: number;
  code: string;
  detail?: unknown;
  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (detail !== undefined) this.detail = detail;
  }
}

interface RawErrorBody {
  error?: string;
  message?: string;
}

function joinPath(base: string, path: string): string {
  const trimmedBase = base.replace(/\/$/, '');
  if (path.startsWith('http')) return path;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

/**
 * Did the server actually answer us, or did we simply never find out?
 *
 * A 4xx IS an answer - "not permitted", "no such thing", "not ready". There is
 * nothing a retry would change and, usually, nothing worth telling the user.
 * Only an unreachable gateway, a 5xx, a timeout, a 429, or a dead network
 * leaves us genuinely not knowing.
 *
 * The distinction matters because the UI must never present "we could not
 * check" as if it were a fact about the thing being checked. The service
 * worker's offline fallback is a synthetic 503, so it counts as unverified.
 */
export function isUnverifiedFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    return (
      error.status === 0 ||
      error.status === 408 ||
      error.status === 429 ||
      error.status >= 500
    );
  }
  // No HTTP answer at all: network error, aborted fetch, unparseable body.
  return true;
}

/**
 * "This deployment has no such ROUTE" vs "the gate said no".
 *
 * Both are 404 `not_found`, and as of 2026-08-01 they are 404 `not_found` ON
 * PURPOSE: a resource in a Workspace you are not a member of must answer
 * exactly like a resource that is not there, or the status is an enumeration
 * oracle (org-auth.ts `resourceRefusalFor`). The UI is the one place that may
 * not be led by that, because several panes read a 404 as "the backend for
 * this feature is not deployed yet" and say so on screen.
 *
 * The one thing that still separates them is measured, not assumed (staging,
 * 2026-08-01): the gateway's own not-found handler echoes the path —
 *   unmounted route  →  {"error":"not_found","path":"/api/…"}
 *   resource gate    →  {"error":"not_found"}
 * — and no handler adds `path`. So a `path` in the body means the router never
 * found a handler, and the absence of one means a handler answered.
 *
 * Use it ONLY to decide whether a "not deployed / feature off" message is
 * honest. It must never be used to tell a user whether a resource exists — the
 * gate deliberately refuses to answer that, and this would answer it.
 */
export function isRouteMissing(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 404) return false;
  const detail = error.detail as { path?: unknown } | undefined;
  return typeof detail?.path === 'string';
}

export interface ApiRequestInit extends RequestInit {
  json?: unknown;
}

const EXPECTED_PRINCIPAL_HEADER = 'X-Chaos-Expected-Principal';
const RESPONSE_PRINCIPAL_HEADER = 'X-Chaos-Response-Principal';

interface CapturedPrincipal {
  readonly userId: string;
  readonly principalFence: string;
  readonly generation: number;
  readonly getCurrent: () => {
    readonly status: string;
    readonly userId: string | null;
    readonly principalFence: string | null;
    readonly generation: number;
  };
  readonly invalidate: (status: 'unverified' | 'unauthenticated') => void;
}

/**
 * Closed mirror of the gateway surfaces that install
 * `requirePrivateCookiePrincipal` before their route handlers.
 *
 * This is deliberately an allowlist rather than "every private-looking
 * apiJson". Some older cookie routes are registered between the gateway's
 * middleware mounts and do not emit a response fence yet. Requiring an echo
 * from those routes would turn a partial security rollout into an outage.
 */
export const UI_COOKIE_PRIVATE_RESPONSE_MANIFEST = {
  prefixes: [
    '/api/sessions/*',
    '/api/fs/*',
    '/api/explorer/*',
    '/api/routing/*',
    '/api/external-links',
    '/api/external-links/*',
    '/api/settings/*',
    '/api/cards/*',
    '/api/notes/*',
    '/api/voice/*',
    '/api/voice-inputs/*',
    '/api/orgs/*',
    '/api/account',
    '/api/account/*',
    '/api/global-roles/*',
    '/api/connectors/*',
    '/api/skills/*',
    '/api/learning/*',
    '/api/permissions/*',
    '/api/templates/*',
    '/api/boards/*',
    '/api/columns/*',
    '/api/triggers/*',
    '/api/sub-tasks/*',
    '/api/sub-task-comments/*',
    '/api/mentions/*',
    '/api/comms/*',
    '/api/inbox',
    '/api/inbox/recent-cards',
    '/api/inbox/counts',
    '/api/notifications/*',
    '/api/push/*',
    '/api/role-messages/*',
    '/api/approval-rules/*',
    '/api/session-asks/*',
    '/api/access-requests/*',
    '/api/imported-sessions/*',
  ],
  exactPaths: [
    'GET /cards/:cardId/live-state',
    'GET /cards/:cardId/message-delivery',
    'GET /kanban/boards',
    'GET /kanban/boards/:boardId',
    'GET /kanban/boards/:boardId/columns',
    'GET /kanban/boards/:boardId/cards',
    'POST /kanban/cards',
    'GET /kanban/cards/:cardId',
    'GET /kanban/cards/:cardId/tab-counts',
    'PATCH /kanban/cards/:cardId',
    'POST /kanban/cards/:cardId/move',
    'POST /kanban/cards/:cardId/move-to-board',
    'DELETE /kanban/cards/:cardId',
    'POST /kanban/cards/:cardId/restore',
    'POST /kanban/boards/:boardId/columns',
    'PATCH /kanban/columns/:columnId',
    'DELETE /kanban/columns/:columnId',
    'GET /kanban/projects',
    'GET /workspaces/:wsId/members',
    'GET /workspaces/me/budget',
    'GET /views/boards/:boardId',
    'PUT /views/boards/:boardId',
    'DELETE /views/boards/:boardId',
    'GET /system/load',
    'GET /system/ai-usage',
  ],
  nativeExemptions: [
    'GET /api/fs/download',
    'GET /api/fs/view',
    'GET /api/explorer/file',
    'GET /api/explorer/download',
    'GET /api/cards/:cardId/files/:filename',
    'GET /api/voice-inputs/:id/audio',
  ],
} as const;

function routeTemplateMatches(route: string, method: string, path: string): boolean {
  const separator = route.indexOf(' ');
  if (route.slice(0, separator) !== method) return false;
  const templateParts = route.slice(separator + 1).split('/');
  const actualParts = path.split('/');
  return templateParts.length === actualParts.length
    && templateParts.every((part, index) => part.startsWith(':') || part === actualParts[index]);
}

function prefixMatches(pattern: string, path: string): boolean {
  if (!pattern.endsWith('/*')) return pattern === path;
  return path.startsWith(pattern.slice(0, -1));
}

function isPrincipalFencedJson(path: string, method: string): boolean {
  if (path.startsWith('http://') || path.startsWith('https://')) return false;
  const pathname = path.split('?', 1)[0] ?? path;
  const gatewayPath = `/api${pathname}`;
  if (UI_COOKIE_PRIVATE_RESPONSE_MANIFEST.nativeExemptions.some(
    (route) => routeTemplateMatches(route, method, gatewayPath),
  )) return false;
  return UI_COOKIE_PRIVATE_RESPONSE_MANIFEST.prefixes.some(
    (prefix) => prefixMatches(prefix, gatewayPath),
  ) || UI_COOKIE_PRIVATE_RESPONSE_MANIFEST.exactPaths.some(
    (route) => routeTemplateMatches(route, method, pathname),
  );
}

async function capturePrivateJsonPrincipal(method: string): Promise<CapturedPrincipal> {
  // Dynamic import breaks the deliberate module cycle: verified-principal
  // uses apiJson for the exempt /auth/me bootstrap, while ordinary apiJson
  // calls consume its already-established memory-only authority here.
  const authority = await import('../verified-principal');
  let current = authority.getVerifiedPrincipalSnapshot();
  if (current.status === 'verifying' && (method === 'GET' || method === 'HEAD')) {
    // A routine same-principal freshness check (a tab return) is in flight: a
    // read waits for it rather than failing for a window that closes by itself.
    // Anything but a clean same-principal success leaves `current` as it was,
    // so the refusal below is unchanged. Unsafe methods never wait.
    const settled = await authority.awaitSamePrincipalVerification();
    if (settled) current = settled;
  }
  if (
    current.status !== 'verified'
    || current.userId === null
    || current.principalFence === null
  ) {
    throw new ApiError(
      428,
      'principal_unverified',
      'A fresh signed-in principal is required before private data can be requested.',
    );
  }
  return {
    userId: current.userId,
    principalFence: current.principalFence,
    generation: current.generation,
    getCurrent: authority.getVerifiedPrincipalSnapshot,
    invalidate: (status) => authority.invalidateVerifiedPrincipal(status),
  };
}

function principalIsStillCurrent(captured: CapturedPrincipal): boolean {
  const current = captured.getCurrent();
  return (
    current.status === 'verified'
    && current.userId === captured.userId
    && current.principalFence === captured.principalFence
    && current.generation === captured.generation
  );
}

function principalResponseError(): ApiError {
  return new ApiError(
    409,
    'principal_response_changed',
    'The signed-in principal changed while this private response was in flight.',
  );
}

function unfencedTransientResponseError(response: Response): ApiError {
  return new ApiError(
    response.status,
    'principal_response_unverified',
    'The private response could not be verified. Retry when the connection recovers.',
  );
}

function isUnfencedTransientResponse(response: Response): boolean {
  return !response.headers.has(RESPONSE_PRINCIPAL_HEADER)
    && (response.status === 408 || response.status === 429 || response.status >= 500);
}

function assertPrivateResponsePrincipal(
  response: Response,
  captured: CapturedPrincipal,
): void {
  const stillCurrent = principalIsStillCurrent(captured);
  if (response.status === 401 && !response.headers.has(RESPONSE_PRINCIPAL_HEADER)) {
    if (stillCurrent) captured.invalidate('unauthenticated');
    throw new ApiError(401, 'unauthorized', 'Authentication required');
  }

  const responsePrincipal = response.headers.get(RESPONSE_PRINCIPAL_HEADER);
  if (
    stillCurrent
    && responsePrincipal === null
    && isUnfencedTransientResponse(response)
  ) {
    // The service worker's bounded network fallback is an unfenced 503, and
    // intermediaries can likewise answer 408/429/5xx before the private route
    // middleware can echo the principal. Never parse or accept that body, but
    // do not turn transport uncertainty into a principal transition either:
    // invalidating here clears the whole QueryClient, unmounting an already
    // accepted card run before its persisted transcript can reconcile.
    throw unfencedTransientResponseError(response);
  }
  if (
    !stillCurrent
    || responsePrincipal !== captured.principalFence
  ) {
    // Never revoke a newer principal B because an older A response arrived.
    if (stillCurrent) captured.invalidate('unverified');
    throw principalResponseError();
  }
}

export async function apiFetch(path: string, init: ApiRequestInit = {}): Promise<Response> {
  const url = joinPath(env.VITE_API_BASE_URL, path);
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const { json, body: rawBody, ...rest } = init;
  let body: BodyInit | null | undefined = rawBody;
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }

  const requestInit: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };
  // /auth/me is the security boundary for the in-memory principal fence.
  // Keep this invariant here as well as at the caller so a future wrapper
  // cannot accidentally make the endpoint service-worker/browser cacheable.
  if (path === '/auth/me' && (init.method === undefined || init.method === 'GET')) {
    requestInit.cache = 'no-store';
    headers.set('Cache-Control', 'no-cache, no-store, max-age=0');
    headers.set('Pragma', 'no-cache');
  }
  if (body !== undefined) requestInit.body = body;

  return fetch(url, requestInit);
}

export async function apiJson<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  // Keep uncovered/public fetch timing byte-identical: no dynamic-import await
  // is introduced unless the gateway is known to enforce the same fence.
  const principal = isPrincipalFencedJson(path, method)
    ? await capturePrivateJsonPrincipal(method)
    : null;
  let requestInit = init;
  if (principal) {
    const headers = new Headers(init.headers);
    const supplied = headers.get(EXPECTED_PRINCIPAL_HEADER);
    if (supplied !== null && supplied !== principal.principalFence) {
      throw principalResponseError();
    }
    headers.set(EXPECTED_PRINCIPAL_HEADER, principal.principalFence);
    requestInit = { ...init, cache: 'no-store', headers };
  }

  const response = await apiFetch(path, requestInit);
  if (principal) assertPrivateResponsePrincipal(response, principal);
  if (!response.ok) {
    let parsed: RawErrorBody = {};
    try {
      parsed = (await response.json()) as RawErrorBody;
    } catch {
      // Empty body or non-JSON — fall through to defaults.
    }
    if (principal && !principalIsStillCurrent(principal)) throw principalResponseError();
    throw new ApiError(
      response.status,
      parsed.error ?? `http_${response.status}`,
      parsed.message ?? response.statusText ?? 'Request failed',
      parsed,
    );
  }
  if (response.status === 204) {
    if (principal && !principalIsStillCurrent(principal)) throw principalResponseError();
    return undefined as unknown as T;
  }
  const parsed = (await response.json()) as T;
  if (principal && !principalIsStillCurrent(principal)) throw principalResponseError();
  return parsed;
}

/** Info passed to an upload `onRetry` callback before each backoff wait. */
export interface UploadRetryInfo {
  /** Zero-based index of the attempt that just failed. */
  attempt: number;
  /** The transient error (ApiError for a 5xx/429, otherwise a network error). */
  error: unknown;
  /** How long we will wait before the next attempt, in ms. */
  nextDelayMs: number;
}

/** Byte counter for an in-flight attempt. `attempt` restarts the bar at 0. */
export interface UploadProgress {
  loaded: number;
  total: number;
  /** Zero-based attempt index — useful for "Retrying (2/4)" copy. */
  attempt: number;
}

export interface ApiUploadOptions {
  /** Upload filename for the multipart part. */
  filename: string;
  /** Multipart field name (default 'file'). */
  fieldName?: string;
  /** Extra multipart fields sent alongside the file. */
  fields?: Record<string, string>;
  /** Retries after the first attempt on a transient failure (default 3). */
  retries?: number;
  /**
   * Hard per-attempt ceiling in ms. Defaults to a SIZE-DERIVED budget, not a
   * flat number: a fixed 180 s would kill a legitimate 50 MB upload on a slow
   * uplink at 180 s, retry it, kill it again — an upload that could have
   * succeeded would then NEVER succeed while burning 4x the bandwidth.
   */
  timeoutMs?: number;
  /**
   * Abort an attempt that stops making upload progress for this long
   * (default 45 s). Only the XHR transport can observe progress, so this is
   * what distinguishes "slow but moving" from "stalled" — the size-derived
   * ceiling above is the fallback for the fetch transport.
   */
  stallTimeoutMs?: number;
  /** Caller cancellation — aborts the in-flight attempt and stops retrying. */
  signal?: AbortSignal;
  /** Fired before each backoff wait. */
  onRetry?: (info: UploadRetryInfo) => void;
  /** Per-attempt byte progress. Providing it selects the XHR transport. */
  onProgress?: (progress: UploadProgress) => void;
}

/**
 * The bytes a caller promised could not be produced.
 *
 * ── WHY THIS TYPE EXISTS (incident 2026-08-15) ────────────────────────
 *
 * An 18-minute recording was refused with an honest 413, and then FOUR
 * automatic retries each sent ZERO bytes, held a gateway socket for ten
 * minutes apiece, and were reported as "Upload failed" — as though the server
 * had refused. The mechanism: `apiUpload` handed an UNREAD Blob to `fetch`
 * inside a FormData and let the network stack read it lazily. When that lazy
 * read produced nothing, nothing here noticed; the failure surfaced as
 * `network_error`, which the retry policy calls TRANSIENT, so the honest
 * refusal was buried under three meaningless ones.
 *
 * Deliberately NOT an `ApiError`: no request was made and the server has no
 * opinion. Deliberately NOT transient either — re-reading a source that just
 * refused to be read is not a plan, and pretending it is was the whole defect.
 *
 * The voice route is not the only place this can happen. Every other upload
 * here takes a `File` the user picked from disk, and a `File` handle goes
 * stale when the file is moved, renamed, deleted, or lives on a volume that
 * has since been unmounted. Reading it then throws `NotReadableError` — which,
 * before this, became `network_error` and was retried three times.
 */
export class UnreadableBodyError extends Error {
  /** Overridden by `VoiceStashUnreadableError` for the recording wording. */
  readonly code: string = 'body_unreadable';
  constructor(
    readonly detail: string,
    readonly expectedBytes: number,
    readonly gotBytes: number,
    message?: string,
  ) {
    super(
      message ??
        'This file could not be read from your device — it may have been moved, ' +
          `renamed or deleted since you picked it. Expected ${expectedBytes} bytes, ` +
          `got ${gotBytes}. (${detail})`,
    );
    this.name = 'UnreadableBodyError';
  }
}

/**
 * Read `[start, end)` of a blob and PROVE the read produced that many bytes.
 *
 * This is the single line that would have turned the 2026-08-15 incident into
 * one honest error message instead of forty minutes of silence. A Blob whose
 * bytes cannot be produced still reports a plausible `.size`; only an explicit
 * read finds out, and only a length check makes the finding actionable.
 *
 * Lives here rather than in `voice-upload` so that every uploader inherits it
 * — the voice path wraps it to keep its own recording-specific wording.
 */
export async function readSliceVerified(
  blob: Blob,
  start: number,
  end: number,
): Promise<Uint8Array> {
  const expected = Math.max(0, end - start);
  let buf: ArrayBuffer;
  try {
    buf = await blob.slice(start, end).arrayBuffer();
  } catch (err) {
    throw new UnreadableBodyError(err instanceof Error ? err.message : String(err), expected, 0);
  }
  if (buf.byteLength !== expected) {
    throw new UnreadableBodyError('short read', expected, buf.byteLength);
  }
  return new Uint8Array(buf);
}

/** Slice size for the pre-send read. One 256 MB contiguous allocation fails
 *  where sixty-four 4 MB ones do not. */
const BODY_READ_CHUNK_BYTES = 4 * 1024 * 1024;

/**
 * Read EVERY byte of `blob` and return an in-memory snapshot of exactly that
 * length, or throw before a single byte touches the network.
 *
 * WHY A SNAPSHOT AND NOT JUST A CHECK. Reading the source to prove it is
 * readable and then handing the ORIGINAL Blob to `fetch` would leave the send
 * relying on a second, unobserved read — which is the exact thing that failed.
 * The Blob constructor COPIES its parts (per spec), so the returned body is
 * backed by bytes this process has already produced and counted. A zero-length
 * body from a non-empty source is therefore not a reachable state.
 *
 * WHAT IT COSTS, said plainly: peak memory of one payload. That is bounded by
 * the server's own ceiling on every route (1 MB avatar, 1 MB logo, 25 MB KB
 * file, 25 MB conductor voice, 50 MB card file, 256 MB explorer upload) and
 * the gateway already buffers roughly the same amount to parse the multipart.
 * The explorer's 256 MB is the one place where that is a real cost; the answer
 * there, if the ceiling is ever raised, is the resumable chunk ingress the
 * voice path uses — NOT dropping this guarantee.
 *
 * AN EMPTY SOURCE IS ALLOWED THROUGH, deliberately. "Never send zero bytes"
 * is the voice rule, where a zero-length recording is meaningless; here a
 * zero-byte file is a real thing a person drags into the Explorer, and the KB
 * ingest already has a name for it (`empty_file`). The invariant this function
 * actually enforces is the one that broke: the body length EQUALS the source
 * length. Callers for whom empty is meaningless (both recorders) check that
 * themselves, in their own words.
 */
export async function readBodyVerified(blob: Blob): Promise<Blob> {
  const total = blob?.size ?? 0;
  if (!blob || typeof blob.slice !== 'function') {
    throw new UnreadableBodyError('not a readable blob', total, 0);
  }
  const parts: BlobPart[] = [];
  let read = 0;
  while (read < total) {
    const end = Math.min(read + BODY_READ_CHUNK_BYTES, total);
    let bytes: Uint8Array;
    try {
      bytes = await readSliceVerified(blob, read, end);
    } catch (err) {
      // Re-frame the slice-level failure against the WHOLE source. "Expected
      // 4194304, got 0" is true of the slice and useless to the person who
      // picked a 28 MB file; the counts they can act on are the source's, plus
      // where it died.
      if (err instanceof UnreadableBodyError) {
        throw new UnreadableBodyError(
          `${err.detail} at byte ${read} of ${total}`,
          total,
          read + err.gotBytes,
        );
      }
      throw err;
    }
    parts.push(bytes as unknown as BlobPart);
    read += bytes.byteLength;
  }
  const snapshot = new Blob(parts, { type: blob.type || 'application/octet-stream' });
  if (snapshot.size !== total) {
    // Belt and braces: the slices all verified, so this can only mean the
    // Blob constructor did not keep what it was given. Say so rather than send.
    throw new UnreadableBodyError('snapshot length mismatch', total, snapshot.size);
  }
  return snapshot;
}

/**
 * Gateway error codes that will never succeed on a retry, whatever status they
 * arrive under.
 *
 * `feature_disabled` comes back as 503 from POST /api/ceo/voice when
 * CONDUCTOR_FILL_ENABLED is off. 503 is in the transient band, so before this
 * the client spent three backoffs re-asking a question whose answer is an
 * environment variable. A flag being off is not weather.
 */
const PERMANENT_UPLOAD_CODES = new Set([
  'feature_disabled',
  'legacy_conductor_retired',
  'file_too_large',
  'unsupported_media_type',
  'storage_quota_exceeded',
]);

/**
 * A refusal the automatic path must never retry, and whose message must reach
 * the user unreplaced.
 *
 * 413 is the case that started this: the first, honest answer was "too large",
 * and re-sending the identical body three more times replaced that answer with
 * a different, wronger failure. A permanent refusal is information, not weather.
 */
export function isPermanentUploadRefusal(error: unknown): boolean {
  if (error instanceof UnreadableBodyError) return true;
  if (!(error instanceof ApiError)) return false;
  if (PERMANENT_UPLOAD_CODES.has(error.code)) return true;
  return (
    error.status === 403 || // not permitted — a retry asks the same gate again
    error.status === 408 || // the server received nothing; re-sending is what hung
    error.status === 410 || // gone: this route has been retired
    error.status === 413 || // too large
    error.status === 415 //   wrong type
  );
}

const DEFAULT_UPLOAD_RETRIES = 3;
const MIN_UPLOAD_TIMEOUT_MS = 180_000;
/**
 * Slowest uplink we will still wait for: 32 KB/s. Above this floor the ceiling
 * scales with the payload, so a big file is not punished for being big.
 */
const UPLOAD_FLOOR_BYTES_PER_SEC = 32 * 1024;
const DEFAULT_STALL_TIMEOUT_MS = 45_000;
const UPLOAD_BACKOFF_BASE_MS = 700;
const UPLOAD_BACKOFF_CAP_MS = 8_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function uploadBackoffMs(attempt: number): number {
  const base = Math.min(UPLOAD_BACKOFF_CAP_MS, UPLOAD_BACKOFF_BASE_MS * 2 ** attempt);
  // Up to +30% jitter so parallel clients don't retry in lockstep.
  return Math.round(base + base * 0.3 * Math.random());
}

/** Per-attempt ceiling for a payload of `bytes`, unless the caller overrides. */
export function uploadTimeoutForSize(bytes: number, override?: number): number {
  if (typeof override === 'number' && override > 0) return override;
  const derived = Math.ceil((bytes / UPLOAD_FLOOR_BYTES_PER_SEC) * 1000);
  return Math.max(MIN_UPLOAD_TIMEOUT_MS, derived);
}

/** 5xx and 429 are worth retrying; other 4xx are caller errors (fail fast). */
function isTransientStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

function parseErrorBody(text: string): RawErrorBody {
  if (!text) return {};
  try {
    return JSON.parse(text) as RawErrorBody;
  } catch {
    return {};
  }
}

/** Marker for a failure the retry policy should treat as transient. */
class TransientUploadError extends Error {
  code: 'upload_timeout' | 'upload_stalled' | 'network_error';
  constructor(code: 'upload_timeout' | 'upload_stalled' | 'network_error', message: string) {
    super(message);
    this.name = 'TransientUploadError';
    this.code = code;
  }
}

interface AttemptContext {
  attempt: number;
  signal: AbortSignal;
}

/**
 * The single retry/backoff policy every uploader shares.
 *
 * `attemptFn` performs ONE upload attempt and must reject with either an
 * `ApiError` (an HTTP answer we got) or a `TransientUploadError` (no usable
 * answer). Everything else is treated as fatal so a genuine bug fails loudly
 * instead of being retried three times.
 */
async function runWithUploadRetry<T>(
  attemptFn: (ctx: AttemptContext) => Promise<T>,
  opts: Pick<ApiUploadOptions, 'retries' | 'signal' | 'onRetry'>,
): Promise<T> {
  const retries = opts.retries ?? DEFAULT_UPLOAD_RETRIES;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (opts.signal?.aborted) {
      throw new ApiError(0, 'upload_cancelled', 'Upload cancelled');
    }
    const controller = new AbortController();
    const onCallerAbort = (): void => controller.abort();
    opts.signal?.addEventListener('abort', onCallerAbort, { once: true });
    try {
      return await attemptFn({ attempt, signal: controller.signal });
    } catch (err) {
      if (opts.signal?.aborted) {
        throw new ApiError(0, 'upload_cancelled', 'Upload cancelled');
      }
      // A permanent refusal wins over the status band it arrived in: a 503
      // `feature_disabled` is in the transient range and is still an answer.
      // Throwing it here is what keeps its message from being replaced by a
      // later, transient-looking failure.
      const retryable =
        !isPermanentUploadRefusal(err) &&
        (err instanceof TransientUploadError ||
          (err instanceof ApiError && isTransientStatus(err.status)));
      if (!retryable) throw err;
      lastError = err;
      if (attempt >= retries) {
        if (err instanceof TransientUploadError) {
          throw new ApiError(0, err.code, err.message, err);
        }
        throw err;
      }
      const delay = uploadBackoffMs(attempt);
      opts.onRetry?.({ attempt, error: err, nextDelayMs: delay });
      await sleep(delay);
    } finally {
      opts.signal?.removeEventListener('abort', onCallerAbort);
    }
  }

  throw lastError instanceof Error ? lastError : new ApiError(0, 'upload_failed', 'Upload failed');
}

/**
 * Wrap an ALREADY-READ body in a fresh FormData.
 *
 * `body` must be the snapshot from `readBodyVerified`, never the caller's
 * original Blob — the whole point is that the bytes handed to the transport
 * have been produced and counted. A fresh FormData per attempt keeps a
 * consumed body from being re-offered.
 */
function buildUploadForm(body: Blob, opts: ApiUploadOptions): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(opts.fields ?? {})) form.append(k, v);
  form.append(opts.fieldName ?? 'file', body, opts.filename);
  return form;
}

/**
 * Upload a Blob as multipart/form-data with automatic retry/backoff on a
 * transient network reset, timeout, 429, or 5xx.
 *
 * A single dropped socket must NOT equal data loss — this is the network half
 * of the voice-recording safety net (the durable half is the IndexedDB stash).
 *
 * EVERY BYTE IS READ AND COUNTED BEFORE THE FIRST REQUEST (`readBodyVerified`),
 * so a source that cannot be produced fails immediately, with the byte counts,
 * and is never retried — see `UnreadableBodyError` and the 2026-08-15 incident.
 *
 * Uses `fetch`, which cannot report upload progress. Pass `onProgress` (or call
 * `apiUploadWithProgress` directly) when a progress bar or stall detection
 * matters — e.g. a 50 MB workspace file.
 *
 * Fails fast (no retry) on a permanent refusal (413/415/410/408/403, or a
 * gateway code that cannot change on a retry) and on caller cancellation via
 * `opts.signal`. Returns the parsed JSON body.
 */
export async function apiUpload<T = unknown>(
  path: string,
  blob: Blob,
  opts: ApiUploadOptions,
): Promise<T> {
  if (opts.onProgress) return apiUploadWithProgress<T>(path, blob, opts);
  // Before the retry loop, so an unreadable source can never be mistaken for
  // a transient network failure and re-sent.
  const body = await readBodyVerified(blob);
  const timeoutMs = uploadTimeoutForSize(body.size, opts.timeoutMs);

  return runWithUploadRetry<T>(async ({ signal }) => {
    let timedOut = false;
    const controller = new AbortController();
    const onAbort = (): void => controller.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const res = await apiFetch(path, {
        method: 'POST',
        body: buildUploadForm(body, opts),
        signal: controller.signal,
      });
      if (res.ok) {
        if (res.status === 204) return undefined as unknown as T;
        return (await res.json()) as T;
      }
      const text = await res.text().catch(() => '');
      const parsed = parseErrorBody(text);
      const fallbackMsg = text ? text.slice(0, 200) : res.statusText || 'Upload failed';
      throw new ApiError(
        res.status,
        parsed.error ?? `http_${res.status}`,
        parsed.message ?? fallbackMsg,
        parsed,
      );
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (timedOut) {
        throw new TransientUploadError('upload_timeout', 'Upload timed out');
      }
      throw new TransientUploadError(
        'network_error',
        err instanceof Error ? err.message : 'Network error',
      );
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }, opts);
}

/**
 * Same policy as `apiUpload`, on an XMLHttpRequest transport so the caller gets
 * byte progress AND a stall timeout.
 *
 * Why the stall timeout matters: with only a total ceiling, a wedged socket
 * either hangs for the whole ceiling (a 50 MB upload can legitimately need
 * ~27 minutes) or a legitimate slow transfer gets killed. Watching progress
 * separates the two — a genuinely stuck upload is abandoned in ~45 s no matter
 * how large the file is, while a slow-but-moving one is left alone.
 *
 * The same pre-send verified read applies here. It matters MORE on this
 * transport, not less: these are the routes a `File` picked from disk goes
 * through, and a stale handle to a moved or deleted file reaches `xhr.send` as
 * an `onerror` — indistinguishable from a dropped socket, and retried as one.
 */
export async function apiUploadWithProgress<T = unknown>(
  path: string,
  blob: Blob,
  opts: ApiUploadOptions,
): Promise<T> {
  const url = joinPath(env.VITE_API_BASE_URL, path);
  const body = await readBodyVerified(blob);
  const ceilingMs = uploadTimeoutForSize(body.size, opts.timeoutMs);
  const stallMs = opts.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;

  return runWithUploadRetry<T>(({ attempt, signal }) => {
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let stallTimer: ReturnType<typeof setTimeout> | null = null;
      // Once the request body is fully handed off, the browser stops reporting
      // progress and we are simply waiting for the server — which may take
      // minutes (KB ingest, a whisper fallback). Watching for "no progress"
      // during that wait would abort perfectly healthy uploads, so the
      // watchdog is retired here and the size-derived ceiling takes over.
      let bodySent = false;

      const cleanup = (): void => {
        if (stallTimer) clearTimeout(stallTimer);
        clearTimeout(ceilingTimer);
        signal.removeEventListener('abort', onAbort);
      };
      const fail = (err: unknown): void => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(err);
      };
      const done = (value: T): void => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      };

      const ceilingTimer = setTimeout(() => {
        try { xhr.abort(); } catch { /* already gone */ }
        fail(new TransientUploadError('upload_timeout', 'Upload timed out'));
      }, ceilingMs);

    /** Rearm the stall watchdog after observed upload progress. */
      const bump = (): void => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = null;
        if (bodySent) return;
        stallTimer = setTimeout(() => {
          try { xhr.abort(); } catch { /* already gone */ }
          fail(
            new TransientUploadError(
              'upload_stalled',
              `Upload stopped making progress for ${Math.round(stallMs / 1000)}s`,
            ),
          );
        }, stallMs);
      };

      const onAbort = (): void => {
        try { xhr.abort(); } catch { /* already gone */ }
        fail(new ApiError(0, 'upload_cancelled', 'Upload cancelled'));
      };
      signal.addEventListener('abort', onAbort, { once: true });

      xhr.open('POST', url);
      xhr.withCredentials = true;
      if (xhr.upload) {
        xhr.upload.onprogress = (e: ProgressEvent): void => {
          bump();
          if (!opts.onProgress || !e.lengthComputable) return;
          opts.onProgress({ loaded: e.loaded, total: e.total, attempt });
        };
        // Body fully handed off — from here the wait is on the server.
        xhr.upload.onloadend = (): void => {
          bodySent = true;
          if (stallTimer) clearTimeout(stallTimer);
          stallTimer = null;
        };
      }
      xhr.onload = (): void => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (xhr.status === 204 || !xhr.responseText) {
            done(undefined as unknown as T);
            return;
          }
          try {
            done(JSON.parse(xhr.responseText) as T);
          } catch {
            fail(new ApiError(xhr.status, 'bad_response', 'Server sent a malformed response'));
          }
          return;
        }
        const parsed = parseErrorBody(xhr.responseText);
        const fallbackMsg = xhr.responseText
          ? xhr.responseText.slice(0, 200)
          : xhr.statusText || 'Upload failed';
        fail(
          new ApiError(
            xhr.status,
            parsed.error ?? `http_${xhr.status}`,
            parsed.message ?? fallbackMsg,
            parsed,
          ),
        );
      };
      xhr.onerror = (): void =>
        fail(new TransientUploadError('network_error', 'Network error during upload'));
      xhr.ontimeout = (): void =>
        fail(new TransientUploadError('upload_timeout', 'Upload timed out'));
      xhr.onabort = (): void => {
        // A deliberate abort has already settled the promise above; this only
        // catches an abort we did not initiate.
        fail(new TransientUploadError('network_error', 'Upload aborted'));
      };

      bump();
      xhr.send(buildUploadForm(body, opts));
    });
  }, opts);
}
